// Your profile photo: your avatar becomes a button (Upload / Remove when you have one), and a picked image
// opens a dialog to frame it — drag to move it, zoom with the slider, the wheel or the +/- buttons. Zooming
// out stops where the photo still fills the square, whatever its shape, so there's never an empty edge. The
// framed square is cropped and compressed here, then uploaded (/api/avatar).
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Camera, Trash2, Upload, ZoomIn, ZoomOut } from '@/components/ui/icons';
import { IMAGE_TYPES } from '@shared/uploads';
import { useDb, useMe } from '@/lib/store';
import { cropSquareImage, readImage } from '@/lib/image';
import { postAvatar } from '@/lib/upload';
import { updateUser } from '@/pages/admin/adminActions';
import { Avatar } from '@/components/ui/Avatar';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem } from '@/components/ui/Menu';
import { Modal } from '@/components/ui/Modal';
import { toast } from '@/components/ui/overlays';

/** Your avatar, with a camera on hover: picks a photo (or offers Upload / Remove once you have one). */
export function EditablePhoto({ size, className }: { size: number; className?: string }) {
  const me = useMe();
  const hasPhoto = useDb((db) => !!db.users[me]?.avatarUrl);
  const [file, setFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pick = () => inputRef.current?.click();
  return (
    <>
      <Popover
        placement="bottom-start"
        disabled={!hasPhoto}
        content={(close) => (
          <Menu>
            <MenuItem icon={<Upload size={15} />} label="Upload a new photo" onClick={() => (close(), pick())} />
            <MenuItem icon={<Trash2 size={15} />} label="Remove photo" danger onClick={() => (close(), updateUser(me, { avatarUrl: null }))} />
          </Menu>
        )}
      >
        {/* sized by the avatar inside it, which grows with the text size preference (base.css zoom) */}
        <button type="button" className={clsx('photo-edit', className)} aria-label={hasPhoto ? 'Change your photo' : 'Add a photo'} onClick={hasPhoto ? undefined : pick}>
          <Avatar userId={me} size={size} />
          <span className="photo-edit-overlay">
            <Camera size={Math.max(16, Math.round(size * 0.26))} />
          </span>
        </button>
      </Popover>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_TYPES.join(',')}
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = ''; // picking the same file again still opens the dialog
          if (f) setFile(f);
        }}
      />
      {file && <PhotoCropModal file={file} onClose={() => setFile(null)} />}
    </>
  );
}

/** The square you frame the photo in, in px. */
const FRAME = 300;
const MAX_ZOOM = 4;

interface Framing {
  /** 1: the photo's short side just fills the square */
  zoom: number;
  /** the photo's centre, offset from the square's centre (px) */
  x: number;
  y: number;
}

function PhotoCropModal({ file, onClose }: { file: File; onClose: () => void }) {
  const me = useMe();
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [src, setSrc] = useState('');
  const [view, setView] = useState<Framing>({ zoom: 1, x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ px: number; py: number; x: number; y: number } | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    readImage(file).then(setImg, (e: Error) => {
      toast(e.message);
      onClose();
    });
    return () => URL.revokeObjectURL(url);
  }, [file]); // eslint-disable-line react-hooks/exhaustive-deps

  const iw = img?.naturalWidth || 1;
  const ih = img?.naturalHeight || 1;
  const cover = FRAME / Math.min(iw, ih);
  // keep the photo covering the square: zoom no lower than 1, and no gap at any edge
  const fit = (v: Framing): Framing => {
    const zoom = Math.min(MAX_ZOOM, Math.max(1, v.zoom));
    const mx = (iw * cover * zoom - FRAME) / 2;
    const my = (ih * cover * zoom - FRAME) / 2;
    return { zoom, x: Math.max(-mx, Math.min(mx, v.x)), y: Math.max(-my, Math.min(my, v.y)) };
  };
  // zoom around the square's centre: what's in the middle stays there
  const zoomTo = (next: (z: number) => number) =>
    setView((v) => {
      const zoom = Math.min(MAX_ZOOM, Math.max(1, next(v.zoom)));
      return fit({ zoom, x: (v.x * zoom) / v.zoom, y: (v.y * zoom) / v.zoom });
    });
  const fitRef = useRef(fit);
  fitRef.current = fit;

  // wheel / trackpad zoom (a native listener, so it can keep the page from scrolling)
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const f = Math.exp(-e.deltaY * 0.002);
      setView((v) => {
        const zoom = Math.min(MAX_ZOOM, Math.max(1, v.zoom * f));
        return fitRef.current({ zoom, x: (v.x * zoom) / v.zoom, y: (v.y * zoom) / v.zoom });
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [img]);

  const save = async () => {
    if (!img || saving) return;
    setSaving(true);
    try {
      // the framed square, in the photo's own pixels
      const scale = cover * view.zoom;
      const side = FRAME / scale;
      const photo = await cropSquareImage(img, (iw - side) / 2 - view.x / scale, (ih - side) / 2 - view.y / scale, side);
      updateUser(me, { avatarUrl: await postAvatar(photo) });
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save your photo.");
      setSaving(false);
    }
  };

  const scale = cover * view.zoom;
  return (
    <Modal
      open
      onClose={onClose}
      title="Your photo"
      width={380}
      className="photo-modal"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn primary" disabled={!img || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div
        ref={frameRef}
        className="photo-crop"
        style={{ width: FRAME, height: FRAME }}
        tabIndex={0}
        aria-label="Photo framing: drag or use the arrow keys to move it"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          drag.current = { px: e.clientX, py: e.clientY, x: view.x, y: view.y };
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (d) setView((v) => fit({ ...v, x: d.x + e.clientX - d.px, y: d.y + e.clientY - d.py }));
        }}
        onPointerUp={() => (drag.current = null)}
        onPointerCancel={() => (drag.current = null)}
        onKeyDown={(e) => {
          const step = e.shiftKey ? 40 : 10;
          const moves: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
          const by = moves[e.key];
          if (!by) return;
          e.preventDefault();
          setView((v) => fit({ ...v, x: v.x + by[0], y: v.y + by[1] }));
        }}
      >
        {img && (
          <img
            src={src}
            alt=""
            draggable={false}
            style={{ width: iw * scale, height: ih * scale, transform: `translate(calc(-50% + ${view.x}px), calc(-50% + ${view.y}px))` }}
          />
        )}
        {/* how it looks in round avatars */}
        <span className="photo-crop-ring" aria-hidden />
      </div>
      <div className="photo-zoom">
        <button type="button" className="icon-btn sm" aria-label="Zoom out" disabled={view.zoom <= 1} onClick={() => zoomTo((z) => z - 0.25)}>
          <ZoomOut size={15} />
        </button>
        <input
          type="range"
          className="slider"
          min={1}
          max={MAX_ZOOM}
          step={0.01}
          value={view.zoom}
          aria-label="Zoom"
          style={{ '--pct': `${((view.zoom - 1) / (MAX_ZOOM - 1)) * 100}%` } as React.CSSProperties}
          onChange={(e) => zoomTo(() => Number(e.target.value))}
        />
        <button type="button" className="icon-btn sm" aria-label="Zoom in" disabled={view.zoom >= MAX_ZOOM} onClick={() => zoomTo((z) => z + 0.25)}>
          <ZoomIn size={15} />
        </button>
      </div>
      <p className="photo-hint">Drag to move your photo, and zoom to frame it.</p>
    </Modal>
  );
}
